import React, { createContext, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate as formatDateShared, toInputDate } from '../utils/date';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  
  const currentLanguage = i18n.language || 'en';
  const isRTL = currentLanguage === 'ar';

  // On mount, try to apply per-user saved language
  useEffect(() => {
    const email = localStorage.getItem('user_email');
    const savedLang = email ? localStorage.getItem(`prefs:${email}:lang`) : null;
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  useEffect(() => {
    // Apply RTL/LTR direction to document
    const root = document.documentElement;
    root.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    root.setAttribute('lang', currentLanguage);
    
    // Add RTL class for Tailwind CSS
    if (isRTL) {
      root.classList.add('rtl');
    } else {
      root.classList.remove('rtl');
    }
    
    // Apply Arabic font for Arabic language
    if (currentLanguage === 'ar') {
      document.body.classList.add('font-arabic');
    } else {
      document.body.classList.remove('font-arabic');
    }
  }, [currentLanguage, isRTL]);

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    // Persist per-user language if user_email is set
    const email = localStorage.getItem('user_email');
    if (email) {
      localStorage.setItem(`prefs:${email}:lang`, language);
    }
  };

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'ar' : 'en';
    changeLanguage(newLanguage);
  };

  // Formatting functions
  const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    if (currentLanguage === 'ar') {
      return `${numericAmount.toLocaleString('ar-IQ')} دينار عراقي`;
    }
    return `${numericAmount.toLocaleString('en-US')} Iraqi Dinar`;
  };

  const formatNumber = (number) => {
    const numericValue = parseFloat(number) || 0;
    return numericValue.toLocaleString(currentLanguage === 'ar' ? 'ar-IQ' : 'en-US');
  };

  // صيغة موحّدة dd/mm/yyyy في اللغتين — النظام محاسبي ويجب أن يقرأ
  // الجميع التاريخ نفسه بالشكل نفسه بصرف النظر عن لغة الواجهة
  const formatDate = (date) => formatDateShared(date);

  // toISOString يحوّل إلى UTC فيُنقص يوماً في الصباح الباكر بتوقيت بغداد؛
  // toInputDate يبني السلسلة من المكوّنات المحلية
  const formatDateForInput = (date) => toInputDate(date);

  const value = {
    currentLanguage,
    isRTL,
    changeLanguage,
    toggleLanguage,
    formatCurrency,
    formatNumber,
    formatDate,
    formatDateForInput,
    availableLanguages: [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'ar', name: 'Arabic', nativeName: 'العربية' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;